<template>
  <div>
    <i18n-t
      :keypath="`notificationFeed.${camelCase(feedEntry.type)}`"
      tag="div"
    >
      <template
        v-if="feedEntry.type === feedEntryType.updateNotification"
        #checkinTitle
      >
        <strong>
          <m-link
            :to="link()"
            inherit-color
            background-on-hover
          >
            {{ feedEntry.notification.title }}
          </m-link>
        </strong>
      </template>
      <template
        v-else-if="[feedEntryType.updateMention, feedEntryType.updateComment].includes(feedEntry.type)"
        #checkInTitle
      >
        <strong>
          <m-link
            :to="link()"
            inherit-color
            background-on-hover
          >
            {{ $t('feedEntryTitle.checkIn', { creator: displayNameOf(feedEntry.update.creator, $t('user.deleted.name')) }) }}
          </m-link>
        </strong>
      </template>
      <template
        v-if="[feedEntryType.goalPropertyValueReferenced, feedEntryType.goalComment, feedEntryType.goalUpdateMention, feedEntryType.goalUpdate, feedEntryType.updateMention, feedEntryType.updateComment, feedEntryType.goalUpdateComment].includes(feedEntry.type)"
        #actor
      >
        <strong>
          <template v-if="feedEntry.actor === null">
            {{ $t('user.deleted.name') }}
          </template>
          <m-link
            v-else
            :to="userLink"
            inherit-color
            background-on-hover
          >
            {{ displayNameOf(feedEntry.actor) }}
          </m-link>
        </strong>
      </template>
      <template
        v-if="[feedEntryType.goalPropertyValueReferenced, feedEntryType.goalComment, feedEntryType.goalUpdateMention, feedEntryType.goalUpdateComment, feedEntryType.goalUpdate, feedEntryType.dataSourceQueryError].includes(feedEntry.type)"
        #goalTitle
      >
        <strong>
          <m-link
            inherit-color
            background-on-hover
            :to="link()"
          >
            {{ feedEntry.goal.title }}
          </m-link>
        </strong>
      </template>
      <template
        v-if="[feedEntryType.goalPropertyValueReferenced].includes(feedEntry.type)"
        #propertyTitle
      >
        <template v-if="feedEntry.propertyValue === null || feedEntry.propertyValue.property === null">
          {{ $t('feedEntryTitle.genericPropertyLabel') }}
        </template>
        <strong v-else>
          {{ textByLang(feedEntry.propertyValue.property.label, userLang) }}
        </strong>
      </template>
      <template
        v-if="[feedEntryType.dataSourceUnhealthy,feedEntryType.dataSourceDead].includes(feedEntry.type)"
        #dataSourceName
      >
        <strong>
          <m-link
            :to="link()"
            inherit-color
            background-on-hover
          >
            {{ feedEntry.dataSource.name }}
          </m-link>
        </strong>
      </template>
      <template
        v-if="[feedEntryType.reportReady].includes(feedEntry.type)"
        #reportSubject
      >
        <strong>
          <m-link
            :to="link()"
            inherit-color
            background-on-hover
          >
            {{ feedEntry.printable.report.subject }}
          </m-link>
        </strong>
      </template>
    </i18n-t>
  </div>
</template>

<script>
import camelCase from 'lodash-es/camelCase';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import { GOAL_DETAILS, GOAL_INSIGHTS, PROFILE, UPDATES_EXPLORER } from '@/route-names';
import { GOAL_INSIGHTS_HEALTH_VIEW, GOAL_INSIGHTS_PERFORMANCE_VIEW, VIEW } from '@/route-params';
import { displayName as displayNameOf } from 'shared/composables/user';
import { feedEntryType, printablePage, routeName } from 'shared/constants.json';
import { textByLang } from 'shared/lib/language';

export default {
  name: 'FeedEntryTitle',
  props: {
    feedEntry: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { userLang } = useLoggedInUser();
    return { userLang, displayNameOf };
  },
  data() {
    return { textByLang, camelCase, feedEntryType };
  },
  computed: {
    userLink() {
      if (this.feedEntry.actor === null) {
        return null;
      }

      return {
        name: PROFILE,
        params: { userId: this.feedEntry.actor.uid },
      };
    },
  },
  methods: {
    getUpdateTemplateId(updateTemplate) {
      if (updateTemplate === null) {
        return '';
      }

      return updateTemplate.uid;
    },
    link() {
      switch (this.feedEntry.type) {
        case feedEntryType.goalPropertyValueReferenced:
        case feedEntryType.goalComment:
        case feedEntryType.goalUpdateMention:
        case feedEntryType.goalUpdate:
        case feedEntryType.goalUpdateComment:
        case feedEntryType.dataSourceQueryError:
          if (this.feedEntry.goal === null) {
            throw new Error(`missing goal reference on feed entry: ${this.feedEntry.uid}`);
          }

          return {
            name: GOAL_DETAILS,
            params: { goalId: this.feedEntry.goal.uid },
          };
        case feedEntryType.updateNotification:
          if (this.feedEntry.notification === null) {
            throw new Error(`missing notification reference on feed entry: ${this.feedEntry.uid}`);
          }

          if (this.feedEntry.notification.updateTemplate === null) {
            return {
              name: UPDATES_EXPLORER,
              query: {
                create: '',
                notificationId: this.feedEntry.notification.uid,
              },
            };
          }

          return {
            name: UPDATES_EXPLORER,
            query: {
              create: '',
              templateId: this.getUpdateTemplateId(this.feedEntry.notification.updateTemplate),
              notificationId: this.feedEntry.notification.uid,
            },
          };
        case feedEntryType.updateMention:
        case feedEntryType.updateComment:
          return {
            name: routeName.updateDetails,
            params: { id: this.feedEntry.update.uid },
          };
        case feedEntryType.dataSourceUnhealthy:
        case feedEntryType.dataSourceDead:
          return {
            name: routeName.dataSourceDetails,
            params: { type: this.feedEntry.dataSource.type },
          };
        case feedEntryType.reportReady:
          if (this.feedEntry.printable === null) {
            throw new Error(`missing printable reference on feed entry: ${this.feedEntry.uid}`);
          }
          switch (this.feedEntry.printable.report.printable.page) {
            case printablePage.healthDashboard:
              return {
                name: GOAL_INSIGHTS,
                query: { [VIEW]: GOAL_INSIGHTS_HEALTH_VIEW },
              };
            case printablePage.dashboard:
              return {
                name: routeName.dashboardDetails,
                params: { pageId: this.feedEntry.printable.report.printable.gridPage.uid },
              };
            case printablePage.progressDashboard:
            default:
              return {
                name: GOAL_INSIGHTS,
                query: { [VIEW]: GOAL_INSIGHTS_PERFORMANCE_VIEW },
              };
          }
        default:
          return null;
      }
    },
  },
};
</script>
